import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A coordinator dashboard's open tab and the list it shows, kept in the URL
 * (`?tab=positions&show=offers_expiring`) so the Overview's links are real
 * links, Back returns to the Overview, and a filtered list can be bookmarked.
 * A tab that is not in `tabs` (a feature switched off, a mistyped link)
 * opens `defaultTab`. Choosing a tab clears the list filter: it belonged to
 * the link that opened the previous one. Only `tab` and `show` are rewritten:
 * every table's own saved search, filter and sort (`apps-q`, `apps-sort`, ...)
 * stays in the URL, so each tab keeps its state and coming back to it shows
 * the rows it had.
 *
 * `aliases` maps a retired tab key to where it moved: `{ tab, show }`. A link
 * to the old key (an email, a guide, a bookmark) opens the new tab with `show`
 * naming the part of it to scroll to, provided the new tab is in `tabs`.
 */
export function useDashboardTab(tabs, defaultTab = 'overview', aliases = {}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = searchParams.get('tab');
    const alias = tabs.includes(requested) ? null : aliases[requested];
    const moved = alias && tabs.includes(alias.tab) ? alias : null;
    const activeTab = moved
        ? moved.tab
        : tabs.includes(requested)
          ? requested
          : defaultTab;
    const show = moved
        ? moved.show || null
        : activeTab === requested
          ? searchParams.get('show')
          : null;

    const selectTab = useCallback(
        (key, section) =>
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('show');
                    if (key === defaultTab) next.delete('tab');
                    else next.set('tab', key);
                    if (section && key !== defaultTab)
                        next.set('show', section);
                    return next;
                },
                { replace: true }
            ),
        [setSearchParams, defaultTab]
    );

    const clearShow = useCallback(
        () =>
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('show');
                    next.set('tab', activeTab);
                    return next;
                },
                { replace: true }
            ),
        [setSearchParams, activeTab]
    );

    return { activeTab, show, selectTab, clearShow };
}

export default useDashboardTab;
